<template>
  <div class="submission-page">
    <div class="page-header">
      <h1>
        <svg class="title-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z"/></svg>
        作品投稿
      </h1>
      <p>分享你的创作，让更多人看到你的作品</p>
    </div>

    <!-- 登录检查 -->
    <div v-if="!isLoggedIn" class="login-required">
      <div class="login-prompt">
        <h3>
          <svg class="title-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
          需要登录
        </h3>
        <p>投稿功能需要登录后才能使用，请先登录您的账户。</p>
        <button @click="goToProfile" class="login-btn">前往登录</button>
      </div>
    </div>

    <!-- 投稿表单 -->
    <div v-else class="submission-content">
      <!-- 作者认证检查 -->
      <div v-if="!isAuthor" class="author-required">
        <div class="author-prompt">
          <div class="prompt-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/><path d="M16 13H8"/><path d="M16 17H8"/><path d="M10 9H8"/></svg>
          </div>
          <h3>需要作者认证</h3>
          <p>投稿功能仅对认证作者开放。请先提交作者认证申请，审核通过后即可投稿。</p>
          <div class="prompt-actions">
            <button @click="goToAuthorApplication" class="btn-primary">申请作者认证</button>
            <button @click="goToProfile" class="btn-secondary">返回个人中心</button>
          </div>
        </div>
      </div>

      <template v-else>
      <!-- 步骤指示器 -->
      <div class="step-indicator">
        <div class="step" :class="{ active: currentStep === 1, completed: currentStep > 1 }">
          <span class="step-number">1</span>
          <span class="step-text">基本信息</span>
        </div>
        <div class="step" :class="{ active: currentStep === 2, completed: currentStep > 2 }">
          <span class="step-number">2</span>
          <span class="step-text">章节管理</span>
        </div>
        <div class="step" :class="{ active: currentStep === 3 }">
          <span class="step-number">3</span>
          <span class="step-text">完成投稿</span>
        </div>
      </div>

      <!-- 步骤1: 基本信息 -->
      <div v-if="currentStep === 1" class="step-content">
        <div class="form-section">
          <h2>
            <svg class="title-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20V3H6.5A2.5 2.5 0 0 0 4 5.5v14z"/><path d="M4 19.5A2.5 2.5 0 0 0 6.5 22H20v-5"/></svg>
            作品基本信息
          </h2>
          
          <form @submit.prevent="submitBasicInfo" class="submission-form">
            <div class="form-row">
              <div class="form-group">
                <label for="title">作品标题 *</label>
                <input
                  type="text"
                  id="title"
                  v-model="formData.title"
                  placeholder="请输入作品标题"
                  required
                  maxlength="100"
                />
                <span class="char-count">{{ formData.title.length }}/100</span>
              </div>

              <div class="form-group">
                <label for="author">作者名 *</label>
                <input
                  type="text"
                  id="author"
                  v-model="formData.author"
                  placeholder="请输入作者名"
                  required
                  maxlength="50"
                />
                <span class="char-count">{{ formData.author.length }}/50</span>
              </div>
            </div>

            <div class="form-group">
              <label for="category">作品分类 *</label>
              <select id="category" v-model="formData.category_id" :disabled="categoriesLoading" required>
                <option value="">{{ categoriesLoading ? '正在加载分类...' : '请选择分类' }}</option>
                <option v-for="category in categories" :key="category.id" :value="category.id">
                  {{ category.name }}
                </option>
              </select>
              <div v-if="categoriesError" class="category-error">
                <span>{{ categoriesError }}</span>
                <button type="button" @click="loadCategories" class="retry-btn">重试</button>
              </div>
            </div>

            <div class="form-group">
              <label for="description">作品描述 *</label>
              <textarea 
                id="description" 
                v-model="formData.description" 
                placeholder="请描述您的作品内容、风格、故事背景等..."
                required
                maxlength="500"
                rows="4"
              ></textarea>
              <span class="char-count">{{ formData.description.length }}/500</span>
            </div>

            <div class="form-group">
              <label for="tags">标签</label>
              <input 
                type="text" 
                id="tags" 
                v-model="formData.tags" 
                placeholder="请输入标签，用逗号分隔，如：热血,冒险,校园"
                maxlength="100"
              />
              <span class="char-count">{{ formData.tags.length }}/100</span>
              <small class="form-hint">标签有助于读者发现您的作品</small>
            </div>

            <div class="form-group">
              <label for="cover">封面图片</label>
              <ImageUpload
                v-model="formData.cover"
                :max-size="5"
                category="image"
                @change="handleCoverChange"
              />
              <small class="form-hint">建议尺寸：300x400像素，支持JPG、PNG、GIF格式，最大5MB</small>
            </div>

            <div class="form-row">
              <div class="form-group">
                <label for="update_status">更新状态</label>
                <select id="update_status" v-model="formData.update_status">
                  <option value="ongoing">连载中</option>
                  <option value="finished">已完结</option>
                  <option value="paused">暂停更新</option>
                </select>
              </div>

              <div class="form-group">
                <label for="is_free">收费模式</label>
                <select id="is_free" v-model="formData.is_free" @change="onFreeStatusChange">
                  <option :value="1">免费</option>
                  <option :value="0">收费</option>
                </select>
              </div>
            </div>

            <div v-if="formData.is_free === 0" class="pricing-section">
              <div class="form-row">
                <div class="form-group">
                  <label for="price">章节价格 (金币) *</label>
                  <input 
                    type="number" 
                    id="price" 
                    v-model.number="formData.price" 
                    placeholder="每章节价格"
                    min="0"
                    step="0.01"
                    required
                  />
                </div>

                <div class="form-group">
                  <label for="free_chapters">免费章节数</label>
                  <input 
                    type="number" 
                    id="free_chapters" 
                    v-model.number="formData.free_chapters" 
                    placeholder="前几章免费"
                    min="0"
                  />
                  <small class="form-hint">0表示全收费，-1表示全免费</small>
                </div>
              </div>
            </div>

            <div class="form-actions">
              <button type="submit" class="btn-primary" :disabled="isSubmitting">
                {{ isSubmitting ? '提交中...' : '下一步：添加章节' }}
              </button>
            </div>
          </form>
        </div>
      </div>

      <!-- 步骤2: 章节管理 -->
      <div v-if="currentStep === 2" class="step-content">
        <div class="chapter-section">
          <div class="section-header">
            <h2>
              <svg class="title-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>
              章节管理
            </h2>
            <div class="header-actions">
              <button @click="sortChapters" class="btn-sort" v-if="chapters.length > 1">
                <svg class="btn-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12a9 9 0 1 1-2.64-6.36"/><polyline points="21 3 21 9 15 9"/></svg>
                排序
              </button>
              <button @click="resetChapterForm(); showAddChapter = true" class="btn-secondary">
                <svg class="btn-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                添加章节
              </button>
            </div>
          </div>

          <!-- 章节列表 -->
          <div v-if="chapters.length > 0" class="chapter-list">
            <div v-for="(chapter, index) in chapters" :key="chapter.id || index" class="chapter-item">
              <div class="chapter-info">
                <h4>第{{ chapter.chapter_number }}话: {{ chapter.title }}</h4>
                <p>页数: {{ chapter.page_count }} | 
                   状态: {{ chapter.is_free ? '免费' : '收费' }} | 
                   {{ chapter.status === 'normal' ? '正常' : '隐藏' }}
                </p>
              </div>
              <div class="chapter-actions">
                <button @click="editChapter(index)" class="btn-edit">编辑</button>
                <button @click="deleteChapter(index)" class="btn-delete">删除</button>
              </div>
            </div>
          </div>

          <div v-else class="empty-chapters">
            <p>还没有添加章节，点击"添加章节"开始创建内容</p>
          </div>

          <!-- 添加/编辑章节表单 -->
          <div v-if="showAddChapter" class="chapter-form-overlay">
            <div class="chapter-form">
              <h3>{{ editingChapterIndex !== -1 ? '编辑章节' : '添加章节' }}</h3>
              
              <form @submit.prevent="saveChapter">
                <div class="form-group">
                  <label for="chapter_title">章节标题 *</label>
                  <input 
                    type="text" 
                    id="chapter_title" 
                    v-model="chapterForm.title" 
                    placeholder="如：第1话 开始"
                    required
                    maxlength="100"
                  />
                </div>

                <div class="form-group">
                  <label for="chapter_number">章节序号 *</label>
                  <input 
                    type="number" 
                    id="chapter_number" 
                    v-model.number="chapterForm.chapter_number" 
                    placeholder="章节序号"
                    min="1"
                    required
                  />
                </div>

                <div class="form-group">
                  <label for="content_type">内容类型</label>
                  <select id="content_type" v-model="chapterForm.content_type">
                    <option value="images">图片</option>
                    <option value="pdf">PDF</option>
                  </select>
                </div>

                <div class="form-group">
                  <label for="content_path">章节内容 *</label>
                  <div class="content-upload-section">
                    <div class="upload-hint-bar">
                      上传本话的漫画图片，按顺序自动排列为每一页
                    </div>

                    <!-- 图片上传模式（真实上传，唯一方式） -->
                    <div class="upload-mode">
                      <div class="multiple-upload">
                        <ImageUpload
                          v-model="tempImageUrl"
                          :max-size="10"
                          category="image"
                          @change="handleChapterImageUpload"
                        />
                        <small class="form-hint">支持 JPG、PNG 格式，单张最大 10MB，可连续上传多张</small>
                      </div>

                      <!-- 已上传的图片列表 -->
                      <div v-if="chapterImages.length > 0" class="uploaded-images">
                        <h4>已上传图片 ({{ chapterImages.length }} 张)</h4>
                        <div class="image-list">
                          <div v-for="(image, index) in chapterImages" :key="index" class="image-item">
                            <img :src="image.fullurl || image.url" :alt="`第${index + 1}页`" class="thumb" />
                            <div class="image-info">
                              <span class="page-number">第{{ index + 1 }}页</span>
                              <span class="file-name">{{ image.name }}</span>
                            </div>
                            <div class="image-actions">
                              <button type="button" @click="moveImageUp(index)" :disabled="index === 0" class="move-btn">↑</button>
                              <button type="button" @click="moveImageDown(index)" :disabled="index === chapterImages.length - 1" class="move-btn">↓</button>
                              <button type="button" @click="removeChapterImage(index)" class="remove-btn">删除</button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="form-row">
                  <div class="form-group">
                    <label for="chapter_is_free">是否免费</label>
                    <select id="chapter_is_free" v-model="chapterForm.is_free">
                      <option :value="1">免费</option>
                      <option :value="0">收费</option>
                    </select>
                  </div>

                  <div class="form-group" v-if="chapterForm.is_free === 0">
                    <label for="chapter_price">章节价格 (金币)</label>
                    <input 
                      type="number" 
                      id="chapter_price" 
                      v-model.number="chapterForm.price" 
                      placeholder="章节价格"
                      min="0"
                      step="0.01"
                    />
                  </div>
                </div>

                <div class="form-group">
                  <label for="chapter_status">章节状态</label>
                  <select id="chapter_status" v-model="chapterForm.status">
                    <option value="normal">正常</option>
                    <option value="hidden">隐藏</option>
                  </select>
                </div>

                <div class="form-actions">
                  <button type="button" @click="cancelChapterEdit" class="btn-secondary">取消</button>
                  <button type="submit" class="btn-primary" :disabled="isSubmittingChapter">
                    {{ isSubmittingChapter ? '保存中...' : '保存章节' }}
                  </button>
                </div>
              </form>
            </div>
          </div>

          <div class="step-actions">
            <button @click="currentStep = 1" class="btn-secondary">上一步</button>
            <button @click="currentStep = 3" class="btn-primary" :disabled="chapters.length === 0">
              下一步：完成投稿
            </button>
          </div>
        </div>
      </div>

      <!-- 步骤3: 完成投稿 -->
      <div v-if="currentStep === 3" class="step-content">
        <div class="completion-section">
          <h2>
            <svg class="title-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
            投稿完成
          </h2>
          
          <div class="submission-summary">
            <h3>投稿摘要</h3>
            <div class="summary-item">
              <strong>作品标题：</strong>{{ formData.title }}
            </div>
            <div class="summary-item">
              <strong>作者：</strong>{{ formData.author }}
            </div>
            <div class="summary-item">
              <strong>章节数量：</strong>{{ chapters.length }} 章
            </div>
            <div class="summary-item">
              <strong>收费模式：</strong>{{ formData.is_free ? '免费' : '收费' }}
            </div>
            <div class="summary-item">
              <strong>更新状态：</strong>{{ getUpdateStatusText(formData.update_status) }}
            </div>
          </div>

          <div class="completion-actions">
            <button @click="currentStep = 2" class="btn-secondary">返回修改</button>
            <button @click="createNewSubmission" class="btn-secondary">再次投稿</button>
            <button @click="goToProfile" class="btn-primary">查看我的作品</button>
          </div>
        </div>
      </div>
      </template>
    </div>
  </div>
</template>

<script>
import { getSubmissionCategories, createSubmission, addChapter, BASE_URL, getUserInfo } from '../api/index';
import ImageUpload from '../components/ImageUpload.vue';

export default {
  name: 'SubmissionPage',
  components: {
    ImageUpload
  },
  data() {
    return {
      isLoggedIn: false,
      isAuthor: false,
      authorLoading: false,
      currentStep: 1,
      isSubmitting: false,
      isSubmittingChapter: false,
      showAddChapter: false,
      editingChapterIndex: -1,
      submissionId: null,
      
      // 分类列表
      categories: [],
      
      // 投稿表单数据
      formData: {
        title: '',
        category_id: '',
        author: '',
        cover: '',
        description: '',
        tags: '',
        update_status: 'ongoing',
        is_free: 1,
        price: 0.00,
        free_chapters: 0
      },
      
      // 章节列表
      chapters: [],
      
      // 章节表单数据
      chapterForm: {
        title: '',
        chapter_number: 1,
        content_type: 'images',
        content_path: '',
        page_count: 0,
        is_free: 1,
        price: 0.00,
        status: 'normal'
      },

      // 图片上传相关
      uploadMethod: 'upload', // 'upload' 或 'manual'
      tempImageUrl: '',
      chapterImages: [], // 存储章节图片信息

      // 分类加载状态
      categoriesLoading: false,
      categoriesError: ''
    };
  },
  
  async mounted() {
    this.checkLoginStatus();
    if (this.isLoggedIn) {
      await this.checkAuthorStatus();
      await this.loadCategories();
      // 设置默认作者名为当前用户
      const username = localStorage.getItem('username');
      if (username) {
        this.formData.author = username;
      }
    }
  },

  watch: {
    // 监听章节数量变化，自动设置下一个章节号
    chapters: {
      handler() {
        if (!this.showAddChapter && this.editingChapterIndex === -1) {
          this.chapterForm.chapter_number = this.chapters.length + 1;
        }
      },
      deep: true
    }
  },
  
  methods: {
    // 检查登录状态
    checkLoginStatus() {
      const token = localStorage.getItem('userToken');
      this.isLoggedIn = !!token;
    },

    // 检查作者认证状态
    async checkAuthorStatus() {
      this.authorLoading = true;
      try {
        const res = await getUserInfo();
        if (res.data && res.data.code === 1) {
          const info = res.data.data || {};
          this.isAuthor = !!info.is_author;
        }
      } catch (error) {
        console.error('获取作者认证状态失败:', error);
      } finally {
        this.authorLoading = false;
      }
    },

    // 跳转到作者认证申请
    goToAuthorApplication() {
      this.$router.push('/author-application');
    },

    // 跳转到个人中心
    goToProfile() {
      this.$router.push('/profile');
    },

    delay(ms) {
      return new Promise(resolve => setTimeout(resolve, ms));
    },
    
    // 加载分类列表
    async loadCategories(retryCount = 2) {
      this.categoriesLoading = true;
      this.categoriesError = '';

      try {
        const res = await getSubmissionCategories();
        if (res.data && res.data.code === 1) {
          const data = res.data.data || [];
          this.categories = Array.isArray(data) ? data : [];
          return;
        }

        throw new Error(res.data?.msg || '获取分类失败');
      } catch (error) {
        console.error('获取分类列表失败:', error);

        if (retryCount > 0) {
          console.warn(`获取分类失败，剩余重试次数: ${retryCount}`);
          await this.delay(800);
          return this.loadCategories(retryCount - 1);
        }

        this.categories = [];
        this.categoriesError = error.message || '获取分类失败';
        alert('获取分类列表失败，请稍后重试');
      } finally {
        this.categoriesLoading = false;
      }
    },

    // 收费状态变化处理
    onFreeStatusChange() {
      if (this.formData.is_free === 1) {
        this.formData.price = 0.00;
        this.formData.free_chapters = 0;
      }
    },

    // 提交基本信息
    async submitBasicInfo() {
      if (this.isSubmitting) return;

      // 作者认证检查
      if (!this.isAuthor) {
        alert('投稿需要先通过作者认证');
        this.goToAuthorApplication();
        return;
      }

      // 表单验证
      if (!this.formData.title.trim()) {
        alert('请输入作品标题');
        return;
      }
      if (!this.formData.category_id) {
        alert('请选择作品分类');
        return;
      }
      if (!this.formData.author.trim()) {
        alert('请输入作者名');
        return;
      }
      if (!this.formData.description.trim()) {
        alert('请输入作品描述');
        return;
      }
      if (this.formData.is_free === 0 && (!this.formData.price || this.formData.price <= 0)) {
        alert('收费作品请设置章节价格');
        return;
      }

      this.isSubmitting = true;
      try {
        const submissionData = {
          row: { ...this.formData }
        };

        const res = await createSubmission(submissionData);
        if (res.data && res.data.code === 1) {
          this.submissionId = res.data.data.id;
          this.currentStep = 2;
          alert('基本信息保存成功！现在可以添加章节内容了。');
        } else {
          throw new Error(res.data?.msg || '提交失败');
        }
      } catch (error) {
        console.error('提交基本信息失败:', error);
        alert('提交失败: ' + (error.message || '未知错误'));
      } finally {
        this.isSubmitting = false;
      }
    },

    // 保存章节
    async saveChapter() {
      if (this.isSubmittingChapter) return;

      // 验证表单
      if (!this.chapterForm.title.trim()) {
        alert('请输入章节标题');
        return;
      }
      if (!this.chapterForm.chapter_number || this.chapterForm.chapter_number < 1) {
        alert('请输入有效的章节序号');
        return;
      }
      if (this.chapterImages.length === 0) {
        alert('请至少上传一张章节图片');
        return;
      }

      // 检查章节序号是否重复（编辑时除外）
      const existingChapter = this.chapters.find((chapter, index) =>
        chapter.chapter_number === this.chapterForm.chapter_number &&
        index !== this.editingChapterIndex
      );
      if (existingChapter) {
        alert(`章节序号 ${this.chapterForm.chapter_number} 已存在，请使用其他序号`);
        return;
      }

      if (this.chapterForm.is_free === 0 && (!this.chapterForm.price || this.chapterForm.price <= 0)) {
        alert('收费章节请设置价格');
        return;
      }

      this.isSubmittingChapter = true;
      try {
        // 内容路径来自真实上传的图片（按当前排序）
        const contentPaths = this.chapterImages
          .map(img => img.url)
          .filter(path => path);

        if (contentPaths.length === 0) {
          alert('请至少上传一张章节图片');
          this.isSubmittingChapter = false;
          return;
        }

        const chapterData = {
          ...this.chapterForm,
          content_type: 'images',
          content_path: JSON.stringify(contentPaths),
          page_count: contentPaths.length
        };

        if (this.editingChapterIndex !== -1) {
          // 编辑现有章节
          this.chapters[this.editingChapterIndex] = { ...chapterData };
          alert('章节更新成功！');
        } else {
          // 添加新章节
          if (this.submissionId) {
            const res = await addChapter(this.submissionId, chapterData);
            if (res.data && res.data.code === 1) {
              this.chapters.push({ ...chapterData, id: res.data.data.id });
              alert('章节添加成功！');
            } else {
              throw new Error(res.data?.msg || '添加章节失败');
            }
          } else {
            // 如果还没有投稿ID，先保存到本地
            this.chapters.push({ ...chapterData });
            alert('章节已暂存，将在投稿时一并提交');
          }
        }

        this.cancelChapterEdit();
      } catch (error) {
        console.error('保存章节失败:', error);
        alert('保存章节失败: ' + (error.message || '未知错误'));
      } finally {
        this.isSubmittingChapter = false;
      }
    },

    // 编辑章节
    editChapter(index) {
      this.editingChapterIndex = index;
      this.chapterForm = { ...this.chapters[index] };

      // 重置图片相关数据
      this.chapterImages = [];
      this.tempImageUrl = '';

      // 将content_path从JSON数组还原为已上传图片列表
      if (this.chapterForm.content_path) {
        try {
          const paths = JSON.parse(this.chapterForm.content_path);
          this.chapterImages = paths.map((path, idx) => ({
            url: path,
            fullurl: path.startsWith('http') ? path : `${BASE_URL}${path}`,
            name: `第${idx + 1}页`,
            size: 0,
            type: 'image/jpeg'
          }));
        } catch (e) {
          this.chapterImages = [];
        }
      }

      this.showAddChapter = true;
    },

    // 删除章节
    deleteChapter(index) {
      if (confirm('确定要删除这个章节吗？')) {
        this.chapters.splice(index, 1);
        alert('章节已删除');
      }
    },

    // 取消章节编辑
    cancelChapterEdit() {
      this.showAddChapter = false;
      this.editingChapterIndex = -1;
      this.resetChapterForm();
    },

    // 重置章节表单
    resetChapterForm() {
      this.chapterForm = {
        title: '',
        chapter_number: this.chapters.length + 1,
        content_type: 'images',
        content_path: '',
        page_count: 0,
        is_free: this.formData.is_free, // 继承作品的收费设置
        price: this.formData.price || 0.00,
        status: 'normal'
      };
      // 清空图片列表
      this.chapterImages = [];
      this.tempImageUrl = '';
    },

    // 批量上传章节（未来扩展功能）
    batchUploadChapters() {
      alert('批量上传功能正在开发中，敬请期待！');
    },

    // 章节排序
    sortChapters() {
      this.chapters.sort((a, b) => a.chapter_number - b.chapter_number);
      alert('章节已按序号排序');
    },

    // 获取更新状态文本
    getUpdateStatusText(status) {
      const statusMap = {
        'ongoing': '连载中',
        'finished': '已完结',
        'paused': '暂停更新'
      };
      return statusMap[status] || status;
    },

    // 创建新投稿
    createNewSubmission() {
      if (confirm('确定要创建新的投稿吗？当前的投稿信息将被重置。')) {
        // 重置所有数据
        this.currentStep = 1;
        this.submissionId = null;
        this.chapters = [];
        this.formData = {
          title: '',
          category_id: '',
          author: localStorage.getItem('username') || '',
          cover: '',
          description: '',
          tags: '',
          update_status: 'ongoing',
          is_free: 1,
          price: 0.00,
          free_chapters: 0
        };
        this.resetChapterForm();
        alert('已重置，可以开始新的投稿了！');
      }
    },

    // 处理封面图片上传
    handleCoverChange(fileInfo) {
      if (fileInfo) {
        console.log('封面图片上传成功:', fileInfo);
        // formData.cover 已经通过 v-model 自动更新为相对路径
        // 这里可以添加额外的处理逻辑，比如显示成功提示
      } else {
        console.log('封面图片已删除');
      }
    },

    // 处理章节图片上传
    handleChapterImageUpload(fileInfo) {
      if (fileInfo) {
        // 添加到章节图片列表
        this.chapterImages.push({
          url: fileInfo.url,
          fullurl: fileInfo.fullurl,
          name: fileInfo.name,
          size: fileInfo.size,
          type: fileInfo.type
        });

        // 更新章节表单的content_path
        this.updateChapterContentPath();

        // 清空临时URL，允许继续上传
        this.tempImageUrl = '';

        console.log('章节图片上传成功:', fileInfo);
      }
    },

    // 更新章节内容路径
    updateChapterContentPath() {
      const paths = this.chapterImages.map(img => img.url);
      this.chapterForm.content_path = JSON.stringify(paths);
      this.chapterForm.page_count = paths.length;
    },

    // 移除章节图片
    removeChapterImage(index) {
      if (confirm('确定要删除这张图片吗？')) {
        this.chapterImages.splice(index, 1);
        this.updateChapterContentPath();
      }
    },

    // 向上移动图片
    moveImageUp(index) {
      if (index > 0) {
        const temp = this.chapterImages[index];
        this.chapterImages[index] = this.chapterImages[index - 1];
        this.chapterImages[index - 1] = temp;
        this.updateChapterContentPath();
      }
    },

    // 向下移动图片
    moveImageDown(index) {
      if (index < this.chapterImages.length - 1) {
        const temp = this.chapterImages[index];
        this.chapterImages[index] = this.chapterImages[index + 1];
        this.chapterImages[index + 1] = temp;
        this.updateChapterContentPath();
      }
    }
  }
};
</script>

<style scoped>
.submission-page {
  width: 100%;
  max-width: 860px;
  margin: 0 auto;
  padding: 18px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 10px;
  color: var(--text);
}

.page-header {
  text-align: center;
  margin-bottom: 24px;
  padding: 22px 24px;
  background: linear-gradient(135deg, var(--brand-deep) 0%, var(--brand) 100%);
  color: #fff;
  border-radius: 8px;
  position: relative;
  overflow: hidden;
}

.page-header h1 {
  margin: 0 0 8px 0;
  font-size: 22px;
  font-weight: 700;
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.page-header p {
  margin: 0;
  opacity: 0.9;
  font-size: 13px;
  position: relative;
  z-index: 1;
}

.title-svg {
  width: 22px;
  height: 22px;
  flex-shrink: 0;
}

/* 登录提示 */
.login-required {
  text-align: center;
  padding: 40px;
}

.login-prompt {
  background: var(--surface-2);
  padding: 24px;
  border-radius: 8px;
  border: 1px solid var(--border);
}

.login-prompt h3 {
  color: var(--text);
  margin-bottom: 12px;
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.login-prompt p {
  color: var(--text-dim);
  margin-bottom: 16px;
  font-size: 13px;
}

.login-btn {
  background: var(--brand);
  color: white;
  border: none;
  padding: 9px 20px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background var(--t-fast), transform var(--t-fast);
}

.login-btn:hover {
  transform: translateY(-1px);
  background: var(--brand-hover);
}

.upload-hint-bar {
  background: var(--brand-soft);
  color: var(--brand);
  font-size: 12px;
  padding: 8px 12px;
  border-radius: 6px;
  margin-bottom: 12px;
}

/* 作者认证提示 */
.author-required {
  text-align: center;
  padding: 40px;
}

.author-prompt {
  background: var(--surface-2);
  padding: 32px 24px;
  border-radius: 8px;
  border: 1px solid var(--border);
}

.prompt-icon {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: var(--brand-soft);
  color: var(--brand);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
}

.prompt-icon svg {
  width: 28px;
  height: 28px;
}

.author-prompt h3 {
  color: var(--text);
  margin-bottom: 10px;
  font-size: 17px;
}

.author-prompt p {
  color: var(--text-dim);
  margin-bottom: 18px;
  font-size: 13px;
}

.prompt-actions {
  display: flex;
  justify-content: center;
  gap: 12px;
  flex-wrap: wrap;
}

/* 步骤指示器 */
.step-indicator {
  display: flex;
  justify-content: center;
  margin-bottom: 28px;
  position: relative;
}

.step-indicator::before {
  content: '';
  position: absolute;
  top: 15px;
  left: 25%;
  right: 25%;
  height: 2px;
  background: var(--border);
  z-index: 1;
}

.step {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 2;
  flex: 1;
  max-width: 150px;
}

.step-number {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: var(--surface-3);
  color: var(--text-dim);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  margin-bottom: 8px;
  transition: all 0.3s ease;
}

.step.active .step-number {
  background: var(--brand);
  color: white;
}

.step.completed .step-number {
  background: var(--success);
  color: white;
}

.step-text {
  font-size: 12px;
  color: var(--text-dim);
  text-align: center;
}

.step.active .step-text {
  color: var(--text);
  font-weight: 600;
}

/* 表单样式 */
.form-section {
  background: var(--surface-2);
  border: 1px solid var(--border);
  padding: 20px;
  border-radius: 8px;
}

.form-section h2 {
  margin: 0 0 18px 0;
  color: var(--text);
  font-size: 17px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 7px;
}

.submission-form {
  max-width: 100%;
  display: grid;
  gap: 18px;
}

.form-group {
  margin-bottom: 16px;
  position: relative;
}

.form-group label {
  display: block;
  margin-bottom: 6px;
  color: var(--text);
  font-weight: 500;
  font-size: 13px;
}

.form-group input,
.form-group select,
.form-group textarea {
  width: 100%;
  padding: 9px 12px;
  background: var(--surface);
  color: var(--text);
  border: 1px solid var(--border);
  border-radius: 6px;
  font-size: 13px;
  transition: all 0.3s ease;
  box-sizing: border-box;
}

.form-group input:focus,
.form-group select:focus,
.form-group textarea:focus {
  outline: none;
  border-color: var(--brand);
  box-shadow: 0 0 0 2px var(--brand-soft);
}

.form-group textarea {
  resize: vertical;
  min-height: 86px;
}

.form-group input::placeholder,
.form-group select::placeholder,
.form-group textarea::placeholder {
  color: rgba(255, 255, 255, 0.55);
}

.form-group input:-ms-input-placeholder,
.form-group select:-ms-input-placeholder,
.form-group textarea:-ms-input-placeholder {
  color: rgba(255, 255, 255, 0.55);
}

.char-count {
  position: absolute;
  right: 10px;
  bottom: -16px;
  font-size: 11px;
  color: var(--text-dim);
}

.form-hint {
  display: block;
  margin-top: 4px;
  font-size: 11px;
  color: var(--text-dim);
}

.category-error {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 6px;
  font-size: 12px;
  color: var(--danger);
}

.retry-btn {
  background: var(--danger);
  color: white;
  border: none;
  border-radius: 4px;
  padding: 2px 10px;
  font-size: 12px;
  cursor: pointer;
}

.retry-btn:hover {
  opacity: 0.85;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 30px;
}

.form-row-three {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 25px;
}

.pricing-section {
  background: var(--surface);
  border: 1px solid var(--border);
  padding: 16px;
  border-radius: 6px;
  margin-top: 12px;
}

/* 按钮样式 */
.form-actions {
  margin-top: 22px;
  text-align: center;
}

.btn-primary {
  background: var(--brand);
  color: white;
  border: none;
  padding: 9px 22px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  margin: 0 6px;
}

.btn-primary:hover:not(:disabled) {
  background: var(--brand-hover);
  transform: translateY(-1px);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-secondary {
  background: var(--surface-3);
  color: var(--text);
  border: 1px solid var(--border);
  padding: 9px 22px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  margin: 0 6px;
}

.btn-secondary:hover {
  background: var(--brand-soft);
  border-color: var(--brand);
  transform: translateY(-1px);
}

.btn-svg {
  width: 15px;
  height: 15px;
  flex-shrink: 0;
}

.btn-edit {
  background: var(--brand-soft);
  color: var(--brand);
  border: 1px solid var(--brand);
  padding: 5px 10px;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  margin-right: 5px;
}

.btn-delete {
  background: var(--danger);
  color: white;
  border: none;
  padding: 5px 10px;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
}

/* 章节管理 */
.chapter-section {
  background: var(--surface-2);
  border: 1px solid var(--border);
  padding: 20px;
  border-radius: 8px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 18px;
}

.section-header h2 {
  margin: 0;
  color: var(--text);
  font-size: 17px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 7px;
}

.header-actions {
  display: flex;
  gap: 8px;
  align-items: center;
}

.btn-sort {
  background: var(--brand);
  color: white;
  border: none;
  padding: 6px 12px;
  border-radius: 5px;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 5px;
}

.btn-sort:hover {
  background: var(--brand-hover);
  transform: translateY(-1px);
}

.chapter-list {
  margin-bottom: 30px;
}

.chapter-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 6px;
  margin-bottom: 8px;
  border-left: 3px solid var(--brand);
}

.chapter-info h4 {
  margin: 0 0 4px 0;
  color: var(--text);
  font-size: 14px;
}

.chapter-info p {
  margin: 0;
  color: var(--text-dim);
  font-size: 12px;
}

.chapter-actions {
  display: flex;
  gap: 5px;
}

.empty-chapters {
  text-align: center;
  padding: 28px;
  color: var(--text-dim);
  background: var(--surface);
  border-radius: 6px;
  border: 1px dashed var(--border-strong);
  font-size: 13px;
}

/* 章节表单弹窗 */
.chapter-form-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(12, 25, 41, 0.94);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.chapter-form {
  background: linear-gradient(180deg, #1a2f4a 0%, #0f1c2e 100%);
  border: 1px solid rgba(96, 165, 250, 0.25);
  padding: 22px;
  border-radius: 12px;
  box-shadow: 0 24px 70px rgba(0, 0, 0, 0.55);
  width: 90%;
  max-width: 560px;
  max-height: 90vh;
  overflow-y: auto;
}

.chapter-form h3 {
  margin: 0 0 16px 0;
  color: var(--text);
  font-size: 17px;
}

/* 步骤操作 */
.step-actions {
  margin-top: 22px;
  text-align: center;
}

/* 完成页面 */
.completion-section {
  background: var(--surface-2);
  border: 1px solid var(--border);
  padding: 22px;
  border-radius: 8px;
  text-align: center;
}

.completion-section h2 {
  color: var(--success);
  margin-bottom: 20px;
  font-size: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
}

.submission-summary {
  background: var(--surface);
  border: 1px solid var(--border);
  padding: 16px;
  border-radius: 6px;
  margin-bottom: 22px;
  text-align: left;
}

.submission-summary h3 {
  margin: 0 0 12px 0;
  color: var(--text);
  font-size: 15px;
}

.summary-item {
  margin-bottom: 8px;
  padding: 6px 0;
  border-bottom: 1px solid var(--border);
  font-size: 13px;
}

.summary-item:last-child {
  border-bottom: none;
}

.completion-actions {
  margin-top: 22px;
}

/* 内容上传相关样式 */
.content-upload-section {
  border: 1px solid var(--border);
  border-radius: 6px;
  overflow: hidden;
}

.upload-method-tabs {
  display: flex;
  background: var(--surface);
  border-bottom: 1px solid var(--border);
}

.tab-btn {
  flex: 1;
  padding: 9px 16px;
  background: transparent;
  border: none;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  color: var(--text-dim);
  transition: all 0.3s ease;
}

.tab-btn.active {
  background: var(--surface-2);
  color: var(--text);
  border-bottom: 2px solid var(--brand);
}

.tab-btn:hover:not(.active) {
  background: var(--surface-3);
}

.upload-mode,
.manual-mode {
  padding: 16px;
}

.multiple-upload {
  margin-bottom: 16px;
}

.uploaded-images {
  margin-top: 16px;
}

.uploaded-images h4 {
  margin: 0 0 12px 0;
  color: var(--text);
  font-size: 14px;
}

.image-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid var(--border);
  border-radius: 5px;
  padding: 8px;
}

.image-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px;
  background: var(--surface);
  border-radius: 5px;
  border: 1px solid var(--border);
}

.image-item .thumb {
  width: 50px;
  height: 66px;
  object-fit: cover;
  border-radius: 3px;
  border: 1px solid var(--border);
}

.image-item .image-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.image-item .page-number {
  font-weight: 600;
  color: var(--text);
  font-size: 13px;
}

.image-item .file-name {
  font-size: 11px;
  color: var(--text-dim);
  word-break: break-all;
}

.image-item .image-actions {
  display: flex;
  gap: 4px;
}

.move-btn {
  background: var(--surface-3);
  color: var(--text-dim);
  border: 1px solid var(--border);
  padding: 3px 7px;
  border-radius: 4px;
  font-size: 11px;
  cursor: pointer;
  min-width: 22px;
}

.move-btn:hover:not(:disabled) {
  background: var(--brand-soft);
  color: var(--brand);
}

.move-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.image-item .remove-btn {
  background: var(--danger);
  color: white;
  border: none;
  padding: 3px 7px;
  border-radius: 4px;
  font-size: 11px;
  cursor: pointer;
}

.image-item .remove-btn:hover {
  opacity: 0.85;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .submission-page {
    margin: 10px;
    padding: 15px;
  }

  .form-row {
    grid-template-columns: 1fr;
  }

  .step-indicator {
    flex-direction: column;
    gap: 10px;
  }

  .step-indicator::before {
    display: none;
  }

  .chapter-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }

  .chapter-actions {
    align-self: flex-end;
  }

  .section-header {
    flex-direction: column;
    gap: 15px;
    align-items: flex-start;
  }

  .image-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .image-item .image-actions {
    align-self: flex-end;
  }

  .upload-method-tabs {
    flex-direction: column;
  }

  .tab-btn {
    border-bottom: 1px solid var(--border);
  }

  .tab-btn.active {
    border-bottom: 1px solid var(--border);
    border-left: 3px solid var(--brand);
  }
}
</style>
